import {Dispatch} from 'redux';

import {setAddressView, setCurrentModal} from '~/shared/store/actions';
import {
  ModalName,
  selectCurrentRestaurantId,
} from '~/shared/store/selectors';
import {getLocalizationService} from '~/shared/services/localisationService';
import {navigateToDefaultStartPage} from '~/shared/services/navigation';
import OrderManager from '~/shared/managers/OrderManager/OrderManager';
import {DeliveryMethods} from '~/shared/consts/restaurantConsts';
import {AddressViewValuesMap} from '~/shared/consts/addressConsts';
import closedImg from '~/assets/images/empty-state-2.png';
import menOnBikeImg from '~/assets/images/address-finder-illustration.png';
import usingAppImg from '~/assets/images/courier-using-app-2-large.png';
import outForDeliveryImg from '~/assets/images/out-for-delivery-large.png';
import {trackEvent} from '~/shared/services/analytics';
import {GeneralErrorModalEventNames, OnlyOpenForModalEventNames, OnlyOpenForModalClickOptions} from '~/shared/services/analytics/analyticsModels';
import store from '~/shared/store';

type GetModalArgsParams = {
  modalName: ModalName;
  dispatch: Dispatch;
  isRestaurant: boolean;
  showCodeButton?: boolean;
};

export const getRestaurantErrorModalArgs = ({
  modalName,
  dispatch,
  isRestaurant,
  showCodeButton,
}: GetModalArgsParams) => {
  const {t} = getLocalizationService();
  
  const closeModal = () => dispatch(setCurrentModal('', null));
  const openServiceCodeModal = () => {
    dispatch(setCurrentModal('service_code_input_modal', {onSuccess: closeModal}));
  };

  const trackClosedResView = () => {
    const resId = selectCurrentRestaurantId(store.getState());
    if (resId) {
      trackEvent('handleCloseRestaurantModal', {eventName: GeneralErrorModalEventNames.CLOSED_RES_VIEW, resId: String(resId)});
    }
  };

  const trackClosedResClick = (clickOptions: 'to_active_restaurants' | 'X') => {
    const resId = selectCurrentRestaurantId(store.getState());
    if (resId) {
      trackEvent('handleCloseRestaurantModal', {eventName: GeneralErrorModalEventNames.CLOSED_RES_CLICK, resId: String(resId), clickOptions});
    }
  };

  const trackFarResView = () => {
    const resId = selectCurrentRestaurantId(store.getState());
    if (resId) {
      trackEvent('handleCloseRestaurantModal', {eventName: GeneralErrorModalEventNames.FAR_RES_VIEW, resId: String(resId)});
    }
  };

  const trackFarResClick = (clickOptions: 'to_active_restaurants' | 'X') => {
    const resId = selectCurrentRestaurantId(store.getState());
    if (resId) {
      trackEvent('handleCloseRestaurantModal', {eventName: GeneralErrorModalEventNames.FAR_RES_CLICK, resId: String(resId), clickOptions});
    }
  };

  const trackOnlyOpenForView = (orderType: 'pickup' | 'delivery') => {
    const resId = selectCurrentRestaurantId(store.getState());
    if (resId) {
      trackEvent('handleOnlyOpenForModal', {eventName: OnlyOpenForModalEventNames.VIEW, resId: String(resId), orderType});
    }
  };

  const trackOnlyOpenForClick = (orderType: 'pickup' | 'delivery', clickOptions: OnlyOpenForModalClickOptions) => {
    const resId = selectCurrentRestaurantId(store.getState());
    if (resId) {
      trackEvent('handleOnlyOpenForModal', {eventName: OnlyOpenForModalEventNames.CLICK, resId: String(resId), orderType, clickOptions});
    }
  };

  if (modalName === 'unavailableRestaurant') {
    return {
      imgSrc: closedImg,
      header: t('restaurant_closed_for_orders'),
      subHeader: t('unavailable_restaurant_body'),
      primaryButton: {
        label: t('find_open_restaurants'),
        onClick: () => {
          trackClosedResClick('to_active_restaurants');
          navigateToDefaultStartPage();
        },
      },
      onMount: trackClosedResView,
      onClose: () => trackClosedResClick('X'),
      ...(showCodeButton && {
        secondaryButton: {label: t('i_have_a_code_from_customer_service'), onClick: openServiceCodeModal},
      }),
    };
  }

  if (modalName === 'unavailableStore') {
    return {
      imgSrc: closedImg,
      header: t('store_closed_for_orders'),
      subHeader: t('unavailable_store_body'),
      primaryButton: {
        label: t('find_open_stores'),
        onClick: () => {
          trackClosedResClick('to_active_restaurants');
          navigateToDefaultStartPage();
        },
      },
      onMount: trackClosedResView,
      onClose: () => trackClosedResClick('X'),
      ...(showCodeButton && {
        secondaryButton: {label: t('i_have_a_code_from_customer_service'), onClick: openServiceCodeModal},
      }),
    };
  }

  if (modalName === 'unavailableDelivery') {
    return {
      imgSrc: usingAppImg,
      header: t('open_only_for_pickup'),
      subHeader: t('delivery_unavailable_pickup_available'),
      primaryButton: {
        label: t('ill_pick_it_up'),
        onClick: () => {
          trackOnlyOpenForClick('pickup', 'pickup');
          OrderManager.changeDeliveryMethod(DeliveryMethods.PICKUP);
          closeModal();
        },
      },
      secondaryButton: {
        label: t('see_who_delivers'),
        onClick: () => {
          trackOnlyOpenForClick('pickup', 'to_active_restaurants');
          navigateToDefaultStartPage();
        },
      },
      onMount: () => trackOnlyOpenForView('pickup'),
    };
  }

  if (modalName === 'unavailablePickup') {
    return {
      imgSrc: outForDeliveryImg,
      header: t('open_only_for_delivery'),
      subHeader: t('pickup_unavailable_delivery_available'),
      primaryButton: {
        label: t('modal_order_delivery'),
        onClick: () => {
          trackOnlyOpenForClick('delivery', 'delivery');
          OrderManager.changeDeliveryMethod(DeliveryMethods.DELIVERY);
          closeModal();
        },
      },
      secondaryButton: {
        label: t('find_another_spot'),
        onClick: () => {
          trackOnlyOpenForClick('delivery', 'to_active_restaurants');
          navigateToDefaultStartPage();
        },
      },
      onMount: () => trackOnlyOpenForView('delivery'),
    };
  }

  if (modalName === 'outOfDeliveryAreaNoPickup') {
    return {
      imgSrc: menOnBikeImg,
      header: isRestaurant ? t('restaurant_not_delivering_to_address') : t('store_not_delivering_to_address'),
      subHeader: t('restaurant_not_delivering_body'),
      primaryButton: {
        label: t('explore_open_spots_delivery'),
        onClick: () => {
          trackFarResClick('to_active_restaurants');
          navigateToDefaultStartPage();
        },
      },
      onMount: trackFarResView,
      onClose: () => trackFarResClick('X'),
    };
  }

  if (modalName === 'outOfDeliveryAreaWithPickup') {
    return {
      imgSrc: menOnBikeImg,
      header: isRestaurant ? t('restaurant_not_delivering_to_address') : t('store_not_delivering_to_address'),
      subHeader: t('restaurant_not_delivering_with_pickup_body'),
      primaryButton: {
        label: t('modal_order_pickup'),
        onClick: () => {
          trackOnlyOpenForClick('pickup', 'pickup');
          OrderManager.changeDeliveryMethod(DeliveryMethods.PICKUP);
          closeModal();
        },
      },
      secondaryButton: {
        label: t('find_delivery_spots'),
        onClick: () => {
          trackOnlyOpenForClick('pickup', 'to_active_restaurants');
          navigateToDefaultStartPage();
        },
      },
      onMount: () => trackOnlyOpenForView('pickup'),
    };
  }

  if (modalName === 'noAddress') {
    return {
      imgSrc: menOnBikeImg,
      header: t('no_address_found'),
      subHeader: t('please_enter_an_address'),
      primaryButton: {
        label: t('add_address'),
        onClick: () => {
          closeModal();
          dispatch(setAddressView(AddressViewValuesMap.ADD));
        },
      },
      secondaryButton: {label: t('view_menu'), onClick: closeModal},
    };
  }

  return null;
};
